// Obstacle IDs per Obstacle.swift:
//   0: Merry Go Round (1x1)
//   1: Park Bench (1x1)
//   2: Seesaw / Teeter Totter (2x1, wide)
//   3: Horsey (1x1)
// maxObstacleID = 3. IDs > 3 fall back to 0 (merrygoround).
// Seesaw at the rightmost column also falls back to 0.

export type ObstacleKind = 'merrygoround' | 'parkbench' | 'seesaw' | 'horsey';

export const MAX_OBSTACLE_ID = 3;

export interface ObstacleSpec {
  readonly id: number;
  readonly kind: ObstacleKind;
  readonly widthInSquares: number;
  readonly heightInSquares: number;
  readonly atlasName: string;
}

const SPECS: ObstacleSpec[] = [
  { id: 0, kind: 'merrygoround', widthInSquares: 1, heightInSquares: 1, atlasName: 'merrygoround' },
  { id: 1, kind: 'parkbench',    widthInSquares: 1, heightInSquares: 1, atlasName: 'parkbench' },
  { id: 2, kind: 'seesaw',       widthInSquares: 2, heightInSquares: 1, atlasName: 'seesaw' },
  { id: 3, kind: 'horsey',       widthInSquares: 1, heightInSquares: 1, atlasName: 'horsey' },
];

export function specForId(id: number): ObstacleSpec {
  const fallback = SPECS[0]!;
  if (id < 0 || id > MAX_OBSTACLE_ID) return fallback;
  return SPECS[id] ?? fallback;
}

export function allObstacleSpecs(): readonly ObstacleSpec[] {
  return SPECS;
}

export class Obstacle {
  readonly spec: ObstacleSpec;
  readonly index: number;
  readonly row: number;
  readonly col: number;
  readonly widthInSquares: number;
  readonly heightInSquares: number;

  constructor(whichObstacle: number, atIndex: number, numSquaresHoriz: number) {
    this.index = atIndex;
    this.row = Math.floor(atIndex / numSquaresHoriz);
    this.col = atIndex % numSquaresHoriz;

    let resolvedId = whichObstacle;
    // Seesaw on rightmost column falls back to merrygoround.
    if (this.col === numSquaresHoriz - 1 && resolvedId === 2) resolvedId = 0;
    if (resolvedId < 0 || resolvedId > MAX_OBSTACLE_ID) resolvedId = 0;

    this.spec = specForId(resolvedId);
    this.widthInSquares = this.spec.widthInSquares;
    this.heightInSquares = this.spec.heightInSquares;
  }

  // Mirrors Obstacle.swift::indexInObstacle.
  indexInObstacle(indexInObs: number, numSquaresHoriz: number): boolean {
    if (indexInObs === this.index) return true;
    if (this.widthInSquares === 2 && indexInObs === this.index + 1) return true;
    if (this.heightInSquares === 2 && indexInObs === this.index + numSquaresHoriz) return true;
    if (
      this.widthInSquares === 2 &&
      this.heightInSquares === 2 &&
      indexInObs === this.index + 1 + numSquaresHoriz
    ) {
      return true;
    }
    return false;
  }

  // Mirrors Obstacle.swift::obstacleInColOrRow.
  obstacleInColOrRow(column: number, inRow: number): { row: boolean; col: boolean } {
    return { row: this.row === inRow, col: this.col === column };
  }
}
